import {
  AccountBlock,
  AccountSwitchBlock,
  BarchartBlock,
  BudgetBlock,
  CategoryTransactionsBlock,
  DonutChartBlock,
  DoubleBarChartBlock,
  DoubleProgressBarBlock,
  SaveExtraBlock,
  SVGImageBlock,
} from '../blocks'
import StepChartBlock from '../blocks/ChartBlocks/StepChartBlock'
import ImageBlock from '../blocks/ImageBlocks/ImageBlock'

import type { Beat, GlobalCopy, InsightsPayload } from '../../core'
import { PlaceholderActionBlock } from '../actionBlocks/PlaceholderActionBlock'
import { MakeTransferActionBlockLayer } from '../actionBlocks/MakeTransferActionBlock/MakeTransferActionBlockLayer'
import { getMakeTransferDrawerTitle } from '../actionBlocks/MakeTransferActionBlock/layout'
import { TransactionHistoryBlock } from '../actionBlocks/TransactionHistoryBlock/TransactionHistoryBlock'
import { getDrawerHeaderTitle } from '../actionBlocks/TransactionHistoryBlock/utils'
import {
  TransactionDetailsBlock,
  useTransactionDetailsPrepare,
} from '../actionBlocks/TransactionDetailsBlock'
// Keys live in ./actionBlocksKey (imported directly by chart blocks/tests too) to avoid a
// circular import between this module and the chart blocks — see that file for details.
import { actionBlocksKey } from './actionBlocksKey'

// Drilldown indexes are not passed down: action blocks read them from
// InsightsV2Store (useInsightsV2Store().actionDrawer).
export interface ActionBlockProps {
  beat: Beat
}

const noop = (_: ActionBlockProps) => {}

// A CTA opens a drawer only when it carries a real `block`. The backend may send
// an empty/absent block (or the literal strings "null"/"undefined") for CTAs
// that trigger something else (e.g. a postMessage) instead of a drilldown.
const NON_BLOCK_VALUES = new Set(['', 'null', 'undefined'])
export const hasDrawerBlock = (block?: string | null): boolean => {
  const normalized = block?.trim().toLowerCase()
  return !!normalized && !NON_BLOCK_VALUES.has(normalized)
}

export interface ActionBlockEntry {
  Component: React.ComponentType<ActionBlockProps>
  usePrepare: (props: ActionBlockProps) => void // required; default to noop in each entry
  // Resolves the drawer title from the clicked CTA's variant + copy (e.g.
  // MakeTransfer reads its variant title from layout.ts). Falls back to the CTA label.
  title?: (variant: string | null | undefined, copy: GlobalCopy) => string
}

// Registry of drawer drilldowns, keyed by the `block` name the backend sends
// on a beat's CTA. To add one: create a component taking ActionBlockProps,
// read the clicked indexes from useInsightsV2Store().actionDrawer, and
// register it here. Add
// `usePrepare` when the block needs to load data before rendering.

export const actionBlocks = {
  [actionBlocksKey.PLACEHOLDER_ACTION_BLOCK]: {
    Component: PlaceholderActionBlock,
    usePrepare: noop,
    title: () => 'Action Block Layer Drilldown',
  },
  [actionBlocksKey.MAKE_TRANSFER_ACTION_BLOCK]: {
    Component: MakeTransferActionBlockLayer,
    usePrepare: noop,
    title: getMakeTransferDrawerTitle,
  },
  [actionBlocksKey.TRANSCATION_HISTORY_ACTION_BLOCK]: {
    Component: TransactionHistoryBlock,
    usePrepare: noop,
    title: getDrawerHeaderTitle,
  },
  [actionBlocksKey.TRANSACTION_DETAILS_ACTION_BLOCK]: {
    Component: TransactionDetailsBlock,
    usePrepare: useTransactionDetailsPrepare,
    title: () => 'Transaction Details',
  },
} as Record<string, ActionBlockEntry>

export const blocks = {
  account_block: AccountBlock,
  account_switch_block: AccountSwitchBlock,
  bar_chart_block: BarchartBlock,
  budget_block: BudgetBlock,
  category_transactions_block: CategoryTransactionsBlock,
  donut_chart_block: DonutChartBlock,
  double_bar_chart_block: DoubleBarChartBlock,
  double_progress_bar_block: DoubleProgressBarBlock,
  image_block: ImageBlock,
  save_extra_block: SaveExtraBlock,
  step_chart_block: StepChartBlock,
  svg_image_block: SVGImageBlock,
} as Record<string, React.ComponentType<{ payload: InsightsPayload; beatGuid?: string }>>
